<template>
  <article :class="classes" @click="emit('click')">
    <div v-if="poster?.large" :class="$style.poster">
      <img :src="poster.large" alt="" />
    </div>

    <div :class="$style.body">
      <div :class="$style.head">
        <div :class="$style.info">
          <h3 :class="$style.name">
            <span>{{ name }}</span>
          </h3>
          <p :class="$style.team">
            <span>{{ subName }}</span>
          </p>
        </div>
        <div v-if="icon" :class="$style.icon">
          <img :src="icons[icon]" :alt="icon" />
        </div>
      </div>

      <p v-if="label" :class="$style.label">
        <span>{{ label }}</span>
      </p>

      <ul v-if="labels.length" :class="$style.labels">
        <li
          v-for="(item, index) in labels"
          :key="index"
          :class="$style.labelItem"
        >
          <span :title="item.name">{{ item.name }}</span>
          <span>+{{ item.rate }}</span>
        </li>
      </ul>

      <div v-if="$slots.footer" :class="$style.footer">
        <slot name="footer" />
      </div>
    </div>

    <slot />
  </article>
</template>

<script setup lang="ts">
import csgoSniperIco from '../../assets/types/sniper-icon.svg?url'
import csgoFighterIco from '../../assets/types/fighter-icon.svg?url'
import dotaMainIco from '../../assets/types/icon-type-main.svg?url'
import dotaMiddleIco from '../../assets/types/icon-type-middle.svg?url'
import dotaSupportIco from '../../assets/types/icon-type-support.svg?url'
import type {
  FantasyCardGrades,
  FantasyCardLabel,
  FantasyCardTypeId,
  FantasyImageUrls
} from '~/types/fantasy'

defineOptions({
  name: 'FantasyCommonCard'
})

const props = withDefaults(
  defineProps<{
    type: FantasyCardGrades
    name: string
    subName: string
    icon: FantasyCardTypeId
    label: string
    poster?: FantasyImageUrls | null
    labels?: FantasyCardLabel[]
  }>(),
  {
    poster: null,
    labels: () => []
  }
)

const emit = defineEmits<{ click: [] }>()

const $style = useCssModule()

const icons: Record<FantasyCardTypeId, string> = {
  sniper: csgoSniperIco,
  fighter: csgoFighterIco,
  main: dotaMainIco,
  middle: dotaMiddleIco,
  support: dotaSupportIco
}

const classes = computed(() => [$style.card, $style[props.type]])
</script>

<style lang="scss" module>
.card {
  --card-border-bg: transparent;
  --card-label-bg: transparent;

  position: relative;
  min-width: 0;
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
  border-radius: 2em;
  background: color-get(--root-rgb-bg-card);

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    z-index: 1;
    border-radius: inherit;
    padding: 2px;
    background: var(--card-border-bg);
    pointer-events: none;
    -webkit-mask:
      linear-gradient(#fff 0 0) content-box,
      linear-gradient(#fff 0 0);
    -webkit-mask-composite: xor;
    mask:
      linear-gradient(#fff 0 0) content-box,
      linear-gradient(#fff 0 0);
    mask-composite: exclude;
  }

  .poster + .body {
    border-radius: 2em;

    &::after {
      content: '';
      position: absolute;
      inset: 0;
      z-index: 1;
      border-radius: inherit;
      padding: 2px;
      background: var(--card-border-bg);
      pointer-events: none;
      -webkit-mask:
        linear-gradient(#fff 0 0) content-box,
        linear-gradient(#fff 0 0);
      -webkit-mask-composite: xor;
      mask:
        linear-gradient(#fff 0 0) content-box,
        linear-gradient(#fff 0 0);
      mask-composite: exclude;
    }

    .head,
    .label,
    .labels,
    .footer {
      position: relative;
      z-index: 2;
    }
  }

  @include down(md) {
    display: flex;
    flex: 1 1 auto;
    flex-direction: column;
    height: 100%;
    min-height: 100%;

    .poster {
      height: 18.6em;
      margin-bottom: -3em;
    }

    .body {
      gap: 1em;
    }

    .head {
      min-height: 4.4em;
      opacity: 0.7;
    }

    .name span {
      @include typo-get(subhead-l);
    }

    .icon {
      width: 7.5em;
      height: 4.4em;
    }

    .labels {
      gap: 0.8em;
    }

    .footer {
      padding-top: 0;
    }
  }
}

.poster {
  position: relative;
  flex-shrink: 0;
  height: 16.8em;
  margin-bottom: -1.2em;
  overflow: hidden;
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: top;
  }
}

.body {
  position: relative;
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 1em;
  padding: 1.6em 0;
  background: color-get(--root-rgb-bg-card);
}

.head {
  display: flex;
  gap: 1em;
  align-items: center;
  min-height: 4.5em;
  padding: 0 1.6em;
}

.info {
  min-width: 0;
  flex: 1;
}

.icon {
  flex-shrink: 0;
  height: 4.5em;
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
}

.name {
  margin-bottom: 0.4em;
  overflow: hidden;
  text-overflow: ellipsis;

  span {
    @include typo-get(headline-s);
    color: color-get(--root-rgb-text-1);
  }
}

.team {
  overflow: hidden;
  text-overflow: ellipsis;

  span {
    @include typo-get(body-s);
    color: color-get(--root-rgb-text-2);
  }
}

.label {
  padding: 0.4em 1.6em;
  text-align: center;
  background: var(--card-label-bg);

  span {
    @include typo-get(body-m);
    color: color-get(--root-rgb-text-inverse);
  }
}

.footer {
  width: 100%;
  margin-top: auto;
  padding: 0 1.6em;
}

.labels {
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 0.4em;
  padding: 0 1.6em;
}

.labelItem {
  display: flex;
  gap: 0.8em;
  align-items: center;
  justify-content: space-between;
  padding: 0.4em;
  border-radius: 0.4em;
  background: color-get(--root-rgb-bg-tone-dark);

  span {
    @include typo-get(body-s);
    color: color-get(--root-rgb-text-2);

    &:first-child {
      min-width: 0;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      flex: 1;
    }

    &:last-child {
      flex-shrink: 0;
    }
  }
}

.bronze {
  --card-border-bg: linear-gradient(90deg, #6e4b2a 0%, #ca6200 100%);
  --card-label-bg: linear-gradient(186.34deg, #ca6200 16.89%, #6e4b2a 65.7%);
}

.silver {
  --card-border-bg: linear-gradient(90deg, #70737b 0%, #c0c4cb 100%);
  --card-label-bg: linear-gradient(183.55deg, #c0c4cb 5.04%, #70737b 81.28%);
}

.gold {
  --card-border-bg: linear-gradient(90deg, #8b4e15 0%, #ffe522 100%);
  --card-label-bg: linear-gradient(185.94deg, #ffe522 4.4%, #8b4e15 92.02%);
}
</style>
